import React, {ChangeEvent, useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    TextField,
    TMultipleSelectValue,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';

export const EditCompanySettingsModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const initialCompanySettings = store.variables.companyInEditing;
    const questionsFrequencyOptions = [{label: 'company.company-modal.questions-frequency.one_every_day', value: 'one_every_day'}, {label: 'company.company-modal.questions-frequency.one_on_even_dates', value: 'one_on_even_dates'}, {label: 'company.company-modal.questions-frequency.weekly', value: 'weekly'}];
    const companyId = store.payload!.companySettingsPayload.companyId;
    const channels = [{label: 'common.channel.email', value: 'email'}, {label: 'common.channel.mobile', value: 'mobile'}, {label: 'common.channel.slack', value: 'slack'}, {label: 'common.channel.teams', value: 'teams'}];
    const answerContextOptions = [{label: 'common.answer-context.BINARY', value: 'BINARY'}, {label: 'common.answer-context.FOUR_OPTIONS', value: 'FOUR_OPTIONS'}, {label: 'common.answer-context.FIVE_OPTIONS', value: 'FIVE_OPTIONS'}];
    const questionsLogicOptions = [{label: 'common.question-logic.default', value: 'DEFAULT'}, {label: 'common.question-logic.shuffle', value: 'SHUFFLE_CATEGORIES'}];

    const [isPersonalDashboardEnabled, setPersonalDashboardEnabled] = useState<boolean>(initialCompanySettings ? initialCompanySettings.isPersonalDashboardEnabled : false);
    const [isMentorEnabled, setMentorEnabled] = useState<boolean>(initialCompanySettings ? initialCompanySettings.isMentorEnabled : false);
    const [isPublicSector, setPublicSector] = useState<boolean>(initialCompanySettings ? initialCompanySettings.isPublicSector : false);
    const [employeesCannotSeeComments, setEmployeesCannotSeeComments] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeComments : false);
    const [employeesCannotSeeTagsCard, setEmployeesCannotSeeTagsCard] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeTagsCard : false);
    const [employeesCannotSeeLocationsCard, setEmployeesCannotSeeLocationsCard] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeLocationsCard : false);
    const [employeesCannotSeeRolesCard, setEmployeesCannotSeeRolesCard] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeRolesCard : false);
    const [employeesCannotSeeServiceLengthsCard, setEmployeesCannotSeeServiceLengthsCard] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeServiceLengthsCard : false);
    const [employeesCannotSeeCategoriesCard, setEmployeesCannotSeeCategoriesCard] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeCategoriesCard : false);
    const [employeesCannotSeeDepartmentsCard, setEmployeesCannotSeeDepartmentsCard] = useState<boolean>(initialCompanySettings ? initialCompanySettings.dataHiddenFromEmployees.employeesCannotSeeDepartmentsCard : false);
    const [questionsFrequency, setQuestionsFrequency] = useState<string>(initialCompanySettings ? initialCompanySettings.questionsFrequency : 'one_every_day');
    const [questionsLogic, setQuestionsLogic] = useState<string>(initialCompanySettings ? initialCompanySettings.questionsLogic : 'DEFAULT');
    const [allowedChannels, setAllowedChannels] = useState<TMultipleSelectValue>(initialCompanySettings ? initialCompanySettings.allowedChannels : ['email', 'mobile', 'slack', 'teams']);
    const [emailName, setEmailName] = useState<string>(initialCompanySettings ? initialCompanySettings.emailName : '');
    const [answerContext, setAnswerContext] = useState<TMultipleSelectValue>(initialCompanySettings ? initialCompanySettings.answerContext : ['BINARY']);
    const [logoFile, setLogoFile] = useState<string | ArrayBuffer | null>(initialCompanySettings ? initialCompanySettings.logoFile : '');
    const [isMachineLearningEnabled, setMachineLearningEnabled] = useState<boolean>(initialCompanySettings ? initialCompanySettings.isMachineLearningEnabled : false);
    const [isBusinessUnitDisablingEnabled, setIsBusinessUnitDisablingEnabled] = useState<boolean>(initialCompanySettings ? initialCompanySettings.isBusinessUnitDisablingEnabled : false);
    const toBase64 = async (file: File): Promise<string | ArrayBuffer | null> => new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);

        reader.onload = (): void => resolve(reader.result);
        reader.onerror = reject;
    });
    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (e.target.files) {
            const newVar = await toBase64(e.target.files[0]);

            setLogoFile(newVar);
        }
    };

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{store.label('company.company-modal.title-settings')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    <TextField
                        label={store.label('company.company-modal.email-name')}
                        defaultValue={emailName}
                        onChange={((value): void => setEmailName(value))}
                        id='company-email-name'
                        name='company-email-name'
                        description={store.variables.showValidation && store.getNameError(emailName, 'company_email_name_forbidden') !== undefined ? store.label(`common.validation.${store.getNameError(emailName, 'company_email_name_forbidden')}`) : undefined}
                        invalid={store.variables.showValidation && store.getNameError(emailName, 'company_email_name_forbidden') !== undefined}
                    />
                    <Select
                        label={store.label('company.company-modal.select-allowed-channels')}
                        defaultValue={allowedChannels}
                        onChange={(value): void => setAllowedChannels(value as TMultipleSelectValue)}
                        options={
                            channels.map((channelOption) => ({
                                label: store.label(`common.channel.${channelOption.value}`),
                                value: channelOption.value,
                            }))
                        }
                        id='select-allowed-channels'
                        name='select-allowed-channels'
                        type={'multiple'}
                        description={store.variables.showValidation && store.checkIfChannelsAreMissing(allowedChannels as TMultipleSelectValue) !== undefined ? store.label(`common.validation.${store.checkIfChannelsAreMissing(allowedChannels as TMultipleSelectValue)}`) : undefined}
                        invalid={store.variables.showValidation && store.checkIfChannelsAreMissing(allowedChannels as TMultipleSelectValue) !== undefined}
                    />
                    <Select
                        label={store.label('company.company-modal.select-answer-context')}
                        defaultValue={answerContext}
                        onChange={(value): void => setAnswerContext(value as TMultipleSelectValue)}
                        options={
                            answerContextOptions.map((option) => ({
                                label: store.label(option.label),
                                value: option.value,
                            }))
                        }
                        id='select-answer-context'
                        name='select-answer-context'
                        type={'multiple'}
                        description={store.variables.showValidation && store.checkIfAnswerContextIsMissing(answerContext as TMultipleSelectValue) !== undefined ? store.label(`common.validation.${store.checkIfAnswerContextIsMissing(answerContext as TMultipleSelectValue)}`) : undefined}
                        invalid={store.variables.showValidation && store.checkIfAnswerContextIsMissing(answerContext as TMultipleSelectValue) !== undefined}
                    />
                    <Select
                        label={store.label('company.company-modal.select-question-frequency')}
                        defaultValue={questionsFrequency}
                        onChange={(value): void => setQuestionsFrequency('' + value)}
                        options={
                            ...questionsFrequencyOptions.map((frequencyOption) => ({
                                label: store.label(`company.company-modal.questions-frequency.${frequencyOption.value}`),
                                value: frequencyOption.value,
                            }))}
                        id='company-select-frequency'
                        name='company-select-frequency'
                        type={undefined}
                    />
                    <Select
                        label={store.label('company.company-modal.choose-question-logic')}
                        defaultValue={questionsLogic}
                        onChange={(value): void => setQuestionsLogic('' + value)}
                        options={
                            ...questionsLogicOptions.map((questionLogicOption) => ({
                                label: store.label(`${questionLogicOption.label}`),
                                value: questionLogicOption.value,
                            }))}
                        id='company-select-question-logic'
                        name='company-select-question-logic'
                        type={undefined}
                    />
                    <Check
                        label={store.label('company.company-modal.is-public-sector')}
                        defaultChecked={isPublicSector}
                        onChange={(checked): void => setPublicSector(checked)}
                        id='is-public-sector'
                        name='is-public-sector'
                        value='is-public-sector'
                    />
                    <h5>{store.label('company.company-modal.title-employee-view')}</h5>
                    <Check
                        label={store.label('company.company-modal.employee-view-comments')}
                        defaultChecked={employeesCannotSeeComments}
                        onChange={(checked): void => setEmployeesCannotSeeComments(checked)}
                        id='cannot-see-comments'
                        name='cannot-see-comments'
                        value='cannot-see-comments'
                    />
                    <Check
                        label={store.label('company.company-modal.employee-view-categories')}
                        defaultChecked={employeesCannotSeeCategoriesCard}
                        onChange={(checked): void => setEmployeesCannotSeeCategoriesCard(checked)}
                        id='cannot-see-categories'
                        name='cannot-see-categories'
                        value='cannot-see-categories'
                    />
                    <Check
                        label={store.label('company.company-modal.employee-view-tags')}
                        defaultChecked={employeesCannotSeeTagsCard}
                        onChange={(checked): void => setEmployeesCannotSeeTagsCard(checked)}
                        id='cannot-see-tags'
                        name='cannot-see-tags'
                        value='cannot-see-tags'
                    />
                    <Check
                        label={store.label('company.company-modal.employee-view-departments')}
                        defaultChecked={employeesCannotSeeDepartmentsCard}
                        onChange={(checked): void => setEmployeesCannotSeeDepartmentsCard(checked)}
                        id='cannot-see-departments'
                        name='cannot-see-departments'
                        value='cannot-see-departments'
                    />
                    <Check
                        label={store.label('company.company-modal.employee-view-roles')}
                        defaultChecked={employeesCannotSeeRolesCard}
                        onChange={(checked): void => setEmployeesCannotSeeRolesCard(checked)}
                        id='cannot-see-roles'
                        name='cannot-see-roles'
                        value='cannot-see-roles'
                    />
                    <Check
                        label={store.label('company.company-modal.employee-view-countries')}
                        defaultChecked={employeesCannotSeeLocationsCard}
                        onChange={(checked): void => setEmployeesCannotSeeLocationsCard(checked)}
                        id='cannot-see-locations'
                        name='cannot-see-locations'
                        value='cannot-see-locations'
                    />
                    <Check
                        label={store.label('company.company-modal.employee-view-tenure')}
                        defaultChecked={employeesCannotSeeServiceLengthsCard}
                        onChange={(checked): void => setEmployeesCannotSeeServiceLengthsCard(checked)}
                        id='cannot-see-tenure'
                        name='cannot-see-tenure'
                        value='cannot-see-tenure'
                    />
                    <h5>{store.label('company.company-modal.title-premium-plan')}</h5>
                    <Check
                        label={store.label('company.company-modal.is-mentor-enabled')}
                        defaultChecked={isMentorEnabled}
                        onChange={(checked): void => setMentorEnabled(checked)}
                        id='is-mentor-enabled'
                        name='is-mentor-enabled'
                        value='is-mentor-enabled'
                    />
                    <Check
                        label={store.label('company.company-modal.is-my-dashboard-enabled')}
                        defaultChecked={isPersonalDashboardEnabled}
                        onChange={(checked): void => setPersonalDashboardEnabled(checked)}
                        id='is-my-dashboard-enabled'
                        name='is-my-dashboard-enabled'
                        value='is-my-dashboard-enabled'
                    />
                    <Check
                        label={store.label('company.company-modal.is-machine-learning-enabled')}
                        defaultChecked={isMachineLearningEnabled}
                        onChange={(checked): void => setMachineLearningEnabled(checked)}
                        id='is-machine-learning-enabled'
                        name='is-machine-learning-enabled'
                        value='is-machine-learning-enabled'
                    />
                    <Check
                        label={store.label('company.company-modal.is-business-unit-disabling-enabled')}
                        defaultChecked={isBusinessUnitDisablingEnabled}
                        onChange={(checked): void => setIsBusinessUnitDisablingEnabled(checked)}
                        id='is-business-unit-disable-enabled'
                        name='is-business-unit-disable-enabled'
                        value='is-business-unit-disable-enabled'
                    />
                    <img src={!logoFile || logoFile instanceof ArrayBuffer ? '' : logoFile} alt={'logo'} className="logo__img"/>
                    <label htmlFor="file-upload" className="button button__inner button--color-green-border">
                        {store.label('company.modal.upload')}
                    </label>
                    <input id="file-upload" type="file" onChange={handleFileChange} style={{display: 'none'}} accept="image/*"/>
                    {store.variables.showValidation && store.variables.validationReason === 'incorrect_filetype' &&
                    <p className="validation__message"> {store.label('company.modal.file.errors.filetype')}</p>}
                    {store.variables.showValidation && store.variables.validationReason === 'file_too_large' &&
                    <p className="validation__message"> {store.label('company.modal.file.errors.size')}</p>}
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white" text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block={true} text={store.label('company.modal.save')}
                            onClick={(): void => store.saveCompanySettings({
                                companyId,
                                emailName,
                                isMentorEnabled,
                                isPersonalDashboardEnabled,
                                isPublicSector,
                                dataHiddenFromEmployees: {
                                    employeesCannotSeeComments,
                                    employeesCannotSeeCategoriesCard,
                                    employeesCannotSeeDepartmentsCard,
                                    employeesCannotSeeLocationsCard,
                                    employeesCannotSeeRolesCard,
                                    employeesCannotSeeServiceLengthsCard,
                                    employeesCannotSeeTagsCard},
                                questionsFrequency,
                                questionsLogic,
                                allowedChannels,
                                answerContext,
                                logoFile: initialCompanySettings?.logoFile === logoFile ? null : logoFile,
                                isMachineLearningEnabled,
                                isBusinessUnitDisablingEnabled,
                            })}/>
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
